import useDebouncedValue from 'hooks/useDebouncedValue';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListInvoicesParams, ListInvoicesSortParams, InvoicesStatuses } from 'store/invoices/types';
import { SortDirections } from 'utils/tableHelpers';

export type DateRange = { startDate?: string; endDate?: string };

export enum InvoicesAppliedFilters {
    QUERY = 'query',
    DATE_RANGE = 'dateRange',
    DATE = 'exactDate',
    CUSTOMER_ID = 'customerId',
}

export type FilterTag = {
    key: InvoicesAppliedFilters;
    label: string;
    value: string;
};

export enum ListInvoicesParamsKeys {
    SORT_DIRECTION = 'sortDirection',
    SORT = 'sort',
    PAGE = 'page',
    SIZE = 'size',
    STATUS = 'listInvoiceStatus',
    START_DATE = 'startDate',
    END_DATE = 'endDate',
    DATE = 'exactDate',
    ARCHIVED = 'archived',
    QUERY = 'query',
    CUSTOMER_ID = 'customerId',
}

const useInvoicesFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get(ListInvoicesParamsKeys.QUERY) ?? '';
    const initialCustomerId = searchParams.get(ListInvoicesParamsKeys.CUSTOMER_ID) ?? undefined;
    const initialSort =
        (searchParams.get(ListInvoicesParamsKeys.SORT) as ListInvoicesSortParams) || ListInvoicesSortParams.CREATED_AT;
    const initialSortDir = (searchParams.get(ListInvoicesParamsKeys.SORT_DIRECTION) as SortDirections) || 'DESC';
    const initialPage = Number(searchParams.get(ListInvoicesParamsKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(ListInvoicesParamsKeys.SIZE)) || 10;
    const initialStatuses = searchParams.getAll(ListInvoicesParamsKeys.STATUS) as InvoicesStatuses[];
    const initialStartDate = searchParams.get(ListInvoicesParamsKeys.START_DATE) || undefined;
    const initialEndDate = searchParams.get(ListInvoicesParamsKeys.END_DATE) || undefined;
    const initialDate = searchParams.get(ListInvoicesParamsKeys.DATE) || undefined;
    const archivedParam = searchParams.get(ListInvoicesParamsKeys.ARCHIVED);
    const initialArchived = archivedParam == null ? false : archivedParam === 'true';

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [customerId, setCustomerId] = useState<string | undefined>(initialCustomerId);
    const [archived, setArchived] = useState<boolean | undefined>(initialArchived);
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);
    const [statuses, setStatuses] = useState<InvoicesStatuses[]>(initialStatuses);
    const [date, setDate] = useState<string | undefined>(initialDate);
    const [dates, setDates] = useState<DateRange>({
        startDate: initialStartDate,
        endDate: initialEndDate,
    });
    const [sortState, setSortState] = useState<{
        sort: ListInvoicesSortParams | undefined;
        sortDirection: SortDirections | undefined;
    }>({
        sort: initialSort,
        sortDirection: initialSortDir,
    });

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: ListInvoicesParams) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            const applyArray = (key: string, values?: (string | number)[] | null) => {
                searchParam.delete(key);
                if (values && values.length) {
                    values.forEach((v) => searchParam.append(key, String(v)));
                }
            };

            apply(ListInvoicesParamsKeys.QUERY, next.query);
            apply(ListInvoicesParamsKeys.CUSTOMER_ID, next.customerId);
            apply(ListInvoicesParamsKeys.SORT, next?.sort);
            apply(ListInvoicesParamsKeys.SORT_DIRECTION, next.sortDirection);
            apply(ListInvoicesParamsKeys.PAGE, next?.page?.toString());
            apply(ListInvoicesParamsKeys.SIZE, next?.size?.toString());
            apply(ListInvoicesParamsKeys.ARCHIVED, next.archived === undefined ? undefined : String(next.archived));
            apply(ListInvoicesParamsKeys.SORT, next.sort);
            apply(ListInvoicesParamsKeys.SORT_DIRECTION, next.sortDirection);
            apply(ListInvoicesParamsKeys.START_DATE, next.startDate);
            apply(ListInvoicesParamsKeys.END_DATE, next.endDate);
            apply(ListInvoicesParamsKeys.DATE, next.exactDate);
            applyArray(ListInvoicesParamsKeys.STATUS, next.listInvoiceStatus as string[] | undefined);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            archived,
            startDate: dates.startDate,
            endDate: dates.endDate,
            listInvoiceStatus: statuses,
            query: debouncedQuery,
            exactDate: date?.toString(),
            customerId,
        });
    }, [
        archived,
        page,
        size,
        sortState.sort,
        statuses,
        sortState.sortDirection,
        writeParams,
        dates.startDate,
        dates.endDate,
        debouncedQuery,
        date,
        customerId,
    ]);

    const appliedParams = useMemo(
        () => ({
            archived,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            startDate: dates.startDate,
            endDate: dates.endDate,
            listInvoiceStatus: statuses,
            query: debouncedQuery || undefined,
            exactDate: date,
            customerId,
        }),
        [
            archived,
            sortState.sort,
            sortState.sortDirection,
            page,
            size,
            dates.startDate,
            dates.endDate,
            statuses,
            debouncedQuery,
            date,
            customerId,
        ]
    );

    const tags: FilterTag[] = useMemo(() => {
        const list: FilterTag[] = [];

        const { startDate, endDate } = dates;

        if (startDate && endDate) {
            list.push({
                key: InvoicesAppliedFilters.DATE_RANGE,
                label: 'Date',
                value: `${moment(startDate).format('YYYY-MM-DD')} - ${moment(endDate).format('YYYY-MM-DD')}`,
            });
        }

        if (date) {
            list.push({
                key: InvoicesAppliedFilters.DATE,
                label: 'Date',
                value: moment(date).format('YYYY-MM-DD'),
            });
        }

        return list;
    }, [dates, date]);

    const clearFilter = useCallback((key: string) => {
        switch (key) {
            case ListInvoicesParamsKeys.QUERY: {
                setQueryInputVal('');
                break;
            }

            case ListInvoicesParamsKeys.PAGE: {
                setPage(0);
                break;
            }

            case ListInvoicesParamsKeys.DATE: {
                setDate(undefined);
                break;
            }

            case ListInvoicesParamsKeys.CUSTOMER_ID: {
                setCustomerId(undefined);
                break;
            }

            case ListInvoicesParamsKeys.STATUS: {
                setStatuses([]);
                break;
            }

            case ListInvoicesParamsKeys.SORT: {
                setSortState({
                    sort: undefined,
                    sortDirection: undefined,
                });
                break;
            }

            case InvoicesAppliedFilters.DATE_RANGE: {
                setDates({
                    startDate: undefined,
                    endDate: undefined,
                });
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setPage(0);
        setArchived(undefined);
        setSortState({
            sort: undefined,
            sortDirection: undefined,
        });
        setDates({
            startDate: undefined,
            endDate: undefined,
        });
        setDate(undefined);
        setQueryInputVal('');
        setCustomerId(undefined);
    }, []);

    return {
        queryInputVal,
        appliedParams,
        tags,
        setStatuses,
        setArchived,
        setPage,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
        setQueryInputVal,
        setDates,
        setDate,
        setCustomerId,
        debouncedQuery,
    };
};

export default useInvoicesFilters;
